// src/contexts/MaintenanceContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const MaintenanceContext = createContext();

export const useMaintenanceContext = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenanceContext doit être utilisé dans MaintenanceProvider');
  }
  return context;
};

export const MaintenanceProvider = ({ children }) => {
  const [isInMaintenance, setIsInMaintenance] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState({
    message: '',
    estimatedTime: '',
    imageVersion: ''
  });
  
  // Référence pour éviter les appels multiples
  const isCheckingRef = useRef(false);
  const lastCheckRef = useRef(0);
  const checkTimeoutRef = useRef(null);

  // Fonction de vérification STABLE (ne change jamais)
  const checkMaintenance = useCallback(async () => {
    const now = Date.now();
    
    // Throttling : ne pas vérifier plus d'une fois toutes les 2 secondes
    if (now - lastCheckRef.current < 2000) {
      return;
    }
    
    // Éviter les vérifications multiples simultanées
    if (isCheckingRef.current) {
      return;
    }
    
    isCheckingRef.current = true;
    lastCheckRef.current = now;
    
    try {
      const response = await fetch(`/api/check_maintenance.php?t=${Date.now()}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la vérification du statut de maintenance');
      }
      
      const data = await response.json();
      
      setIsInMaintenance(data.maintenance);
      setMaintenanceInfo({
        message: data.message || '',
        estimatedTime: data.estimated_time || '',
        imageVersion: data.image_version || ''
      });
      
      // console.log('✅ Vérification maintenance:', {
        // mode: data.maintenance ? 'ACTIVÉ' : 'DÉSACTIVÉ',
        // timestamp: new Date(data.timestamp * 1000).toLocaleString('fr-FR')
      // });
      
    } catch (error) {
      // console.error('❌ Erreur vérification maintenance:', error);
      setIsInMaintenance(false);
    } finally {
      isCheckingRef.current = false;
    }
  }, []); // ⚠️ VIDE : la fonction ne change JAMAIS

  // Vérification initiale au montage (UNE SEULE FOIS)
  useEffect(() => {
    // console.log('🔧 MaintenanceContext initialisé');
    checkMaintenance();
  }, []); // ⚠️ Dépendances vides = exécution unique

  // Polling toutes les 5 minutes
  useEffect(() => {
    if (isInMaintenance) {
      return; // Arrêter le polling si en maintenance
    }

    const interval = setInterval(() => {
      // console.log('⏰ Vérification automatique (5 min)');
      checkMaintenance();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isInMaintenance, checkMaintenance]);

  // Fonction pour forcer une vérification STABLE
  const forceCheck = useCallback(() => {
    // Annuler le timeout précédent
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    // Debounce de 1 seconde
    checkTimeoutRef.current = setTimeout(() => {
      checkMaintenance();
    }, 1000);
  }, [checkMaintenance]);

  // Valeur mémorisée pour éviter les re-renders
  const value = useRef({
    isInMaintenance,
    maintenanceInfo,
    forceCheck
  });

  // Mettre à jour la référence sans causer de re-render
  value.current.isInMaintenance = isInMaintenance;
  value.current.maintenanceInfo = maintenanceInfo;

  return (
    <MaintenanceContext.Provider value={value.current}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export default MaintenanceContext;